import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Check, Close } from '@mui/icons-material';

/**
 * Parse residue string format "chain:resName:resNum:insertionCode"
 */
export const parseResidue = (residueStr) => {
  const parts = residueStr.split(':');
  return {
    chain: parts[0] || '',
    resName: parts[1] || '',
    resNum: parseInt(parts[2]) || 0,
    insertionCode: parts[3] || ''
  };
};

/**
 * Group residues by chain and prepare data structure
 */
export const groupResiduesByChain = (residues) => {
  const residuesByChain = {};
  
  // Parse and group residues
  residues.forEach((residueStr, index) => {
    const parsed = parseResidue(residueStr);
    const chain = parsed.chain;
    
    if (!residuesByChain[chain]) {
      residuesByChain[chain] = {
        regular: {},
        insertions: {},
        residueMap: new Map() // Map position to residue data
      };
    }
    
    // Store residue data with original index
    const residueData = {
      ...parsed,
      original: residueStr,
      index: index,
      name: parsed.resName,
      number: parsed.resNum
    };
    
    // Separate insertions from regular residues
    if (parsed.insertionCode) {
      if (!residuesByChain[chain].insertions[parsed.resNum]) {
        residuesByChain[chain].insertions[parsed.resNum] = [];
      }
      residuesByChain[chain].insertions[parsed.resNum].push(residueData);
    } else {
      residuesByChain[chain].regular[parsed.resNum] = residueData;
      residuesByChain[chain].residueMap.set(parsed.resNum, residueData);
    }
  });
  
  // Sort insertions by insertion code
  Object.values(residuesByChain).forEach(chainData => {
    Object.values(chainData.insertions).forEach(insertionList => {
      insertionList.sort((a, b) => a.insertionCode.localeCompare(b.insertionCode));
    });
  });
  
  return residuesByChain;
};

/**
 * Gap indicator between non-consecutive residue rows
 */
export const GapIndicator = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      my: theme.spacing(0.5),
      gap: theme.spacing(1)
    }}>
      <Box sx={{ 
        flex: 1,
        height: 1, 
        backgroundColor: theme.palette.text.disabled,
        opacity: 0.3
      }} />
      <Typography variant="caption" sx={{ 
        fontSize: theme.typography.fontSize.xs, 
        color: theme.palette.text.disabled,
        fontStyle: 'italic'
      }}>
        •••
      </Typography>
      <Box sx={{ 
        flex: 1,
        height: 1, 
        backgroundColor: theme.palette.text.disabled,
        opacity: 0.3
      }} />
    </Box>
  );
};

/**
 * Row of exactly 10 residue boxes
 */
export const ResidueRow = ({ residues, onToggle, isEditable = false }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      gap: theme.spacing(0.375),
      mb: theme.spacing(0.375)
    }}>
      {residues.map(res => (
        <ResidueBox
          key={`pos-${res.number}`}
          residue={res}
          isSelected={res.selected}
          onClick={onToggle}
          isEditable={isEditable && res.hasResidue}
        />
      ))}
    </Box>
  );
};

/**
 * Individual residue box component
 */
export const ResidueBox = ({ residue, isSelected, onClick, isEditable = false }) => {
  const theme = useTheme();
  
  const handleClick = () => {
    if (isEditable && onClick && residue.index !== undefined) {
      onClick(residue.index);
    }
  };
  
  // Determine tooltip content
  const tooltipContent = residue.hasResidue 
    ? `${residue.chain}:${residue.name}:${residue.number}${residue.insertionCode || ''}`
    : `Position ${residue.number}`;
  
  return (
    <Tooltip title={tooltipContent}>
      <Box
        onClick={handleClick}
        sx={{
          width: theme.spacing(5.25),
          height: theme.spacing(3.25),
          border: '1px solid',
          borderColor: isSelected ? theme.palette.selection?.border || theme.palette.primary.main : theme.palette.divider,
          borderRadius: theme.spacing(0.5),
          backgroundColor: isSelected 
            ? theme.palette.selection?.background || (theme.palette.primary.light + '30')
            : theme.palette.grey[50],
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: residue.hasResidue ? (isSelected ? 1 : 0.5) : 0.3,
          cursor: isEditable ? 'pointer' : 'default',
          transition: 'all 0.2s',
          '&:hover': isEditable ? {
            borderColor: theme.palette.primary.main,
            backgroundColor: isSelected 
              ? theme.palette.selection?.backgroundLight || (theme.palette.primary.light + '50')
              : theme.palette.action.selected,
            opacity: residue.hasResidue ? (isSelected ? 1 : 0.7) : 0.4
          } : {}
        }}
      >
        {residue.hasResidue ? (
          // Show name and number for all existing residues (selected or deselected)
          <>
            <Typography sx={{ 
              fontSize: theme.typography.fontSize.xs, 
              fontWeight: theme.typography.fontWeight.semibold,  // Keep same font weight for both
              lineHeight: 1, 
              color: isSelected ? theme.palette.primary.dark : theme.palette.text.secondary
            }}>
              {residue.name}
            </Typography>
            <Typography sx={{ 
              fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
              lineHeight: 1, 
              color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary
            }}>
              {residue.number}{residue.insertionCode || ''}
            </Typography>
          </>
        ) : (
          // Empty position - show only number
          <Typography sx={{ 
            fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
            color: theme.palette.text.disabled 
          }}>
            {residue.number}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * Insertion row component
 */
export const InsertionRow = ({ position, residues, selectedSet, onToggle, isEditable = false }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      mb: theme.spacing(0.375),
      ml: theme.spacing(1.5)
    }}>
      <Typography variant="caption" sx={{ 
        fontSize: theme.typography.fontSize.xs,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        minWidth: theme.spacing(9.5)
      }}>
        Insertions at {position}:
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.spacing(0.375) }}>
        {residues.map(res => (
          <Tooltip 
            key={`${res.number}${res.insertionCode}`}
            title={`${res.chain}:${res.name}:${res.number}${res.insertionCode}`}
          >
            <Box
              onClick={() => isEditable && onToggle && onToggle(res.index)}
              sx={{
                width: theme.spacing(5.25),
                height: theme.spacing(3.25),
                border: '1px solid',
                borderColor: theme.palette.warning.main,
                borderRadius: theme.spacing(0.5),
                backgroundColor: selectedSet.has(res.index)
                  ? theme.palette.warning.light
                  : theme.palette.warning.light + '30',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEditable ? 'pointer' : 'default',
                opacity: selectedSet.has(res.index) ? 1 : 0.5,
                transition: 'all 0.2s',
                '&:hover': isEditable ? {
                  opacity: 1,
                  backgroundColor: theme.palette.warning.light
                } : {}
              }}
            >
              <Typography sx={{ 
                fontSize: theme.typography.fontSize.xs, 
                fontWeight: theme.typography.fontWeight.semibold, 
                lineHeight: 1, 
                color: theme.palette.warning.dark 
              }}>
                {res.name}
              </Typography>
              <Typography sx={{ 
                fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
                lineHeight: 1, 
                color: theme.palette.warning.main 
              }}>
                {res.number}{res.insertionCode}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Full residue visualization component with fixed 10-box rows
 */
export const ResiduesVisualization = ({ 
  residues, 
  selectedSet, 
  onToggle, 
  isEditable = false,
  onToggleAll,
  onToggleChain
}) => {
  const theme = useTheme();
  
  if (!residues || residues.length === 0) {
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          p: 2, 
          textAlign: 'center', 
          color: theme.palette.text.secondary 
        }}
      >
        No residues found in this set
      </Typography>
    );
  }
  
  const residuesByChain = groupResiduesByChain(residues);
  const chains = Object.keys(residuesByChain).sort();
  
  return (
    <Box>
      {/* Overall controls */}
      {isEditable && onToggleAll && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: theme.spacing(1),
          pb: theme.spacing(1),
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedSet.size}/{residues.length} residues selected
          </Typography>
          <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
            <IconButton 
              size="small" 
              onClick={() => onToggleAll(true)}
              title="Select All"
            >
              <Check sx={{ fontSize: theme.dimensions?.icon?.small || 18 }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => onToggleAll(false)}
              title="Deselect All"
            >
              <Close sx={{ fontSize: theme.dimensions?.icon?.small || 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}
      
      {/* Chain sections */}
      {chains.map((chain) => {
        const chainData = residuesByChain[chain];
        const regularResidues = chainData.regular;
        const insertions = chainData.insertions;
        
        // Calculate which rows to show (only rows with at least one residue)
        const rowsToShow = new Set();
        Object.keys(regularResidues).forEach(resNumStr => {
          const resNum = parseInt(resNumStr);
          const rowStart = Math.floor((resNum - 1) / 10) * 10 + 1;
          rowsToShow.add(rowStart);
        });
        
        // Sort row starts
        const sortedRowStarts = Array.from(rowsToShow).sort((a, b) => a - b);
        
        // Generate rows with gaps
        const rows = [];
        let lastRowEnd = 0;
        
        sortedRowStarts.forEach(rowStart => {
          // Check if we need a gap indicator
          if (lastRowEnd > 0 && rowStart > lastRowEnd + 10) {
            rows.push({ type: 'gap' });
          }
          
          // Generate complete row with all 10 positions
          const rowResidues = [];
          for (let pos = rowStart; pos < rowStart + 10; pos++) {
            const residueData = regularResidues[pos];
            if (residueData) {
              rowResidues.push({
                ...residueData,
                selected: selectedSet.has(residueData.index),
                hasResidue: true
              });
            } else {
              // Empty position
              rowResidues.push({
                number: pos,
                selected: false,
                hasResidue: false
              });
            }
          }
          
          rows.push({ type: 'residues', residues: rowResidues });
          
          // Check for insertions in this row range
          for (let pos = rowStart; pos < rowStart + 10; pos++) {
            if (insertions[pos] && insertions[pos].length > 0) {
              rows.push({ 
                type: 'insertions', 
                position: pos, 
                residues: insertions[pos]
              });
            }
          }
          
          lastRowEnd = rowStart + 9;
        });
        
        // Count selected residues in this chain
        const chainResidues = [...Object.values(regularResidues), ...Object.values(insertions).flat()];
        const chainSelected = chainResidues.filter(r => selectedSet.has(r.index)).length;
        
        return (
          <Box key={chain} sx={{ mb: theme.spacing(2) }}>
            {/* Chain header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: theme.spacing(0.5)
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: theme.typography.fontWeight.medium,
                  fontSize: theme.typography.fontSize.sm,
                  display: 'block'
                }}
              >
                Chain {chain} ({chainSelected}/{chainResidues.length} selected)
              </Typography>
              {isEditable && onToggleChain && (
                <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
                  <IconButton 
                    size="small" 
                    onClick={() => onToggleChain(chain, true)}
                    title={`Select all in chain ${chain}`}
                    sx={{ p: theme.spacing(0.25) }}
                  >
                    <Check sx={{ fontSize: theme.dimensions?.icon?.tiny || 14 }} />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => onToggleChain(chain, false)}
                    title={`Deselect all in chain ${chain}`}
                    sx={{ p: theme.spacing(0.25) }}
                  >
                    <Close sx={{ fontSize: theme.dimensions?.icon?.tiny || 14 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            {/* Residue rows */}
            {rows.map((row, idx) => {
              if (row.type === 'gap') {
                return <GapIndicator key={`gap-${idx}`} />;
              }
              
              if (row.type === 'insertions') {
                return (
                  <InsertionRow 
                    key={`insertions-${row.position}`}
                    position={row.position}
                    residues={row.residues}
                    selectedSet={selectedSet}
                    onToggle={onToggle}
                    isEditable={isEditable}
                  />
                );
              }
              
              return (
                <ResidueRow 
                  key={`row-${idx}`}
                  residues={row.residues}
                  onToggle={onToggle}
                  isEditable={isEditable}
                />
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default ResiduesVisualization;